import { useRef, useState, type ReactNode } from "react";
import type { BookSearchResult } from "@pagemates/shared";
import { Avatar } from "./ui";
import { shelf } from "@pagemates/shared/copy";

const txt = shelf.book3d;

/** Page-block thickness in px from a page count: a 275-page book is about 13px at shelf scale. */
export const thicknessFor = (pages: number | null) => Math.max(7, Math.min(22, Math.round((pages ?? 300) / 22)));

const HOLD_MS = 320;

/**
 * A book as a small box: the cover (Hardcover's image, or a generated spine
 * with the title) is the front face; a page block is the side face whose
 * thickness follows the page count; a spine face closes the left side. The
 * whole thing is turned a few degrees so the fore edge shows. Hovering (with
 * a mouse) or pressing and holding (on touch) swings the cover open on its
 * spine; a tap or click is left to `onClick`.
 */
/** What the title page says about the club's copy. */
export interface TitlePage { dates: string | null; days: number | null; stars: number | null; count: number; stamp: string | null }
/** The inside of an on-deck book: the suggester's note (or the blurb's first line), its length in club terms, and the library card. */
export interface Flyleaf { note: string | null; by: string | null; blurb: string | null; length: string | null; card: { name: string; image: string | null; read: boolean }[]; total: number }

/** `open` opens it from outside (the club creation flow's finish); `inner` replaces the inside page's content. */
export function Book3D({ book, page, flyleaf, selected, onClick, open, inner, className }:
  { book: BookSearchResult; page?: TitlePage; flyleaf?: Flyleaf; selected?: boolean; onClick?: () => void; open?: boolean; inner?: ReactNode; className?: string }) {
  const t = thicknessFor(book.pages);
  const authors = book.authors.length ? book.authors.join(", ") : "";
  const [held, setHeld] = useState(false);
  const timer = useRef<number | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  const fired = useRef(false);

  const cancel = () => { if (timer.current) { window.clearTimeout(timer.current); timer.current = null; } start.current = null; setHeld(false); };
  const onPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType === "mouse") return; // hover does this on a mouse
    fired.current = false; start.current = { x: e.clientX, y: e.clientY };
    timer.current = window.setTimeout(() => { fired.current = true; setHeld(true); }, HOLD_MS);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!start.current || held) return;
    if (Math.hypot(e.clientX - start.current.x, e.clientY - start.current.y) > 8) cancel(); // a scroll, not a hold
  };

  return (
    <button type="button" className={`bk3${selected ? " sel" : ""}${held || open ? " open" : ""}${className ? ` ${className}` : ""}`} style={{ "--t": `${t}px` } as React.CSSProperties}
      onClick={() => { if (fired.current) { fired.current = false; return; } onClick?.(); }}
      onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={cancel} onPointerCancel={cancel} onPointerLeave={cancel}
      onContextMenu={(e) => { if (start.current || held) e.preventDefault(); }}
      aria-label={book.title}>
      <span className="bk3-box">
        <span className="face back" />
        <span className="face spine" />
        <span className="face pages" />
        {inner ? <span className="face page1 inner" aria-hidden="true">{inner}</span> : flyleaf ? (
          <span className="face page1 flyleaf" aria-hidden="true">
            {flyleaf.note
              ? <span className="inscr"><span className="q">“{flyleaf.note}”</span>{flyleaf.by && <span className="by">— {flyleaf.by}</span>}</span>
              : flyleaf.blurb ? <span className="inscr blurb">{flyleaf.blurb}</span> : <><b>{book.title}</b>{authors && <i>{authors}</i>}</>}
            {flyleaf.length && <><span className="rule" /><u>{flyleaf.length.split(" · ").map((part, k) => <span key={k} className="lp">{k > 0 && <span className="sep"> · </span>}{part}</span>)}</u></>}
            {flyleaf.card.length > 0 && (
              <span className="card">
                <span className="hd">{txt.wantToRead}</span>
                {/* One ruled line of faces, you first; a check on anyone who has read it before. The count is under the book. */}
                <span className="card-faces">{flyleaf.card.map((c, k) => <span key={k} className={`cf${c.read ? " read" : ""}`} title={c.read ? txt.readIt(c.name) : c.name}><Avatar name={c.name} image={c.image} /></span>)}</span>
              </span>
            )}
          </span>
        ) : (
          <span className="face page1" aria-hidden="true">
          <b>{book.title}</b>{authors && <i>{authors}</i>}
          {page && (page.dates || page.stars !== null) && <span className="rule" />}
          {page?.dates && <u>{page.dates}{page.days ? txt.days(page.days) : ""}</u>}
          {page && (page.stars !== null ? <u className="rt"><em>★</em> {page.stars.toFixed(1)} · {page.count} {page.count === 1 ? txt.rating : txt.ratings}</u> : <u>{txt.unrated}</u>)}
          {page?.stamp && <span className="stamp">{page.stamp}</span>}
          </span>
        )}
        <span className={`face cv${book.coverUrl ? "" : " blank"}`}>
          {book.coverUrl ? <img src={book.coverUrl} alt="" loading="lazy" draggable={false} /> : <span className="spine-title">{book.title}</span>}
          <span className="inside" />
        </span>
        <span className="shadow" />
      </span>
    </button>
  );
}
